import { DataType, DateTime } from '@mode-switch/express';
import { EncField, Encoding } from '@mode-vegas/vegas';
import * as angular from 'angular';
import { merge } from 'lodash';

import { SelectedChartService } from '@mode/charts/contract';

import { ChartFormatHelpers, EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { OrganizationResource } from '@mode/legacy/data-access-model-js';
import { ChartTypes } from '@mode/shared/contract-common';
import { ConfirmDialogService } from '@mode/shared/ui';
import { FlatTableColorScheme, getDensitySelectOptions } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledFormula, Switch } from '@mode/shared/util-switch';
import { validateYAxis } from '../../../../lib/chart-helpers';
import { ChartTitle } from '../../../../lib/charts/chart-title';

export type XFormatType = 'NONE' | 'NUMBER' | 'DATE' | 'MONTH' | 'WEEKDAY' | 'HOUR' | 'MINUTE' | 'SECOND';

class ChartFormatController implements angular.IController {
  static $inject = ['AnalyticsService', 'ConfirmDialogService', '$scope'];

  readonly JS_CustomizationText =
    'Adding custom JavaScript will make certain chart features unavailable (e.g. custom colors, sorts, table calculations, etc).';
  readonly JS_CustomizationDisabledTip =
    'JavaScript customization is unavailable for charts using newer enhancements (e.g. custom colors, sorts, table calculations, etc).';
  readonly DisplayLabelTooltipContent =
    'Display label changes apply to the axis title, tooltip, data point view, and legend series for the field.';

  format: ChartTypes.ChartFormat;
  encoding: Encoding;
  chartType: ChartTypes.ChartType;
  vizToken: string;
  renderModel: RenderDataModel;
  readOnly: boolean;
  chartService: SelectedChartService;
  owner: OrganizationResource;

  mod: ChartTypes.ChartFormat; // Copy of the format parameter for angular model binding
  dimension: CompiledFormula;
  isHorizontal = false;
  isTableType = false;

  allowHighCharts = true;
  muzeEnabledChartTypes: unknown;
  showUseMuzeRenderer = false;

  Y1_AXIS = 'yAxis';
  Y2_AXIS = 'y2Axis';
  yAxisHasError: object = {};
  xFormatType: XFormatType = 'NONE';

  colorSchemes = FlatTableColorScheme.getSchemes();

  densities = getDensitySelectOptions();

  rotations = [
    { name: 'Auto', value: null },
    { name: '0°', value: 0 },
    { name: '+45°', value: 45 },
    { name: '+90°', value: 90 },
    { name: '-45°', value: -45 },
    { name: '-90°', value: -90 },
  ];

  formats = [
    { name: 'Percent', value: 'percent' },
    { name: 'Currency', value: 'currency' },
    { name: 'Thousands', value: 'thousands' },
    { name: 'Millions', value: 'millions' },
    { name: 'Billions', value: 'billions' },
    { name: 'None', value: 'none' },
  ];

  precisions = [
    { name: '0', value: 0 },
    { name: '0.1', value: 1 },
    { name: '0.01', value: 2 },
    { name: '0.001', value: 3 },
  ];

  dateFormats = [
    { name: 'Auto', value: 'auto' },
    { name: 'YYYY-MM-DD HH:MM:SS', value: 'hour' },
    { name: 'Mon YYYY', value: 'full_mon' },
    { name: 'Day Mon YYYY', value: 'full_day' },
    { name: 'Mon Day YYYY', value: 'usa_full' },
    { name: 'MM/DD/YY', value: 'usa_default' },
    { name: 'MM/DD', value: 'mon_day' },
    { name: 'YYYY', value: 'year' },
    { name: 'None', value: 'none' },
  ];

  legendPositions = [
    { name: 'Auto', value: 'auto' },
    { name: 'Top', value: 'top' },
    { name: 'Right', value: 'right' },
    { name: 'Bottom', value: 'bottom' },
    { name: 'Left', value: 'left' },
  ];

  scaleTypes = [
    { name: 'Linear', value: 'linear' },
    { name: 'Logarithmic', value: 'logarithmic' },
  ];

  axisLabels = [
    { name: 'Continuous range', value: 'continuousRange' },
    { name: 'First value only', value: 'firstValueOnly' },
  ];

  defaultConfigs = {
    bar: {
      planeType: 'cartesian',
      hideableValues: true,
    },
    hBar: {
      planeType: 'cartesian',
      hideableValues: true,
    },
    stackedBar: {
      planeType: 'cartesian',
      hideableValues: true,
    },
    stackedBar100: {
      planeType: 'cartesian',
      hideableValues: true,
    },
    hStackedBar: {
      planeType: 'cartesian',
      hideableValues: true,
    },
    hStackedBar100: {
      planeType: 'cartesian',
      hideableValues: true,
    },
    line: {
      planeType: 'cartesian',
      nullToZeroCheckbox: true,
    },
    linePlusBar: {
      planeType: 'cartesian',
      y2Axis: true,
      nullToZeroCheckbox: true,
    },
    area: {
      planeType: 'cartesian',
      yNumberRangeMin: false,
      nullToZeroCheckbox: true,
    },
    totalArea: {
      planeType: 'cartesian',
      yNumberRange: false,
      yNumberFormat: false,
    },
    scatter: {
      planeType: 'cartesian',
      chooseXRange: true,
    },
    pie: {
      planeType: 'polar',
      colorPaletteDropup: false,
    },
    donut: {
      planeType: 'polar',
      colorPaletteDropup: false,
    },
    histogram: {
      planeType: 'cartesian',
      yNumberRange: false,
      yNumberFormat: false,
      nullToZeroCheckbox: false,
      hideableValues: false,
      showAxisLabels: true,
      showNumberFormat: true,
      showScaleType: false,
      showLegendFormatOptions: false,
    },
  };

  configs = this.defaultConfigs;

  displayName = {
    selectedField: undefined,
    options: [],
  };

  useMuzeRenderer = true;

  constructor(
    private analyticsService: AnalyticsService,
    private confirmDialogService: ConfirmDialogService,
    private $scope: angular.IScope
  ) {}

  get config() {
    return {
      yNumberRangeMin: true,
      yNumberRange: true,
      yNumberFormat: true,
      colorPaletteDropup: true,
      allowMuzeRenderer: true,
      showScaleType: true,
      showLegendFormatOptions: true,
      ...this.configs[this.chartType],
    };
  }

  get showDetailsGroup() {
    return (
      this.chartType !== 'table' &&
      this.chartType !== 'histogram' &&
      (this.config.nullToZeroCheckbox || this.owner.plan_code === 'plus')
    );
  }

  $onInit() {
    this.validateYAxisMod(this.Y1_AXIS, false);
    this.validateYAxisMod(this.Y2_AXIS, false);
  }

  $onChanges(changes: angular.IOnChangesObject) {
    this.useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(this.chartType, this.format);
    this.isTableType = this.chartType === 'table';
    this.showUseMuzeRenderer = ChartFormatHelpers.isHighChartTypeEnabledForMuze(this.chartType);

    const highChartsDisabledReasons = ChartFormatHelpers.canRenderWithHighCharts(
      this.chartType,
      this.encoding,
      this.renderModel
    );
    this.allowHighCharts = highChartsDisabledReasons.length === 0;

    if (!this.format) {
      return;
    }
    this.mod = angular.copy(this.format);

    this.dimension = null;
    if (this.encoding && this.renderModel) {
      this.isHorizontal = this.encoding.marks.channel === 'x';

      const { secondary, secondaryDiscrete } = EncodingHelpers.extractPrimarySecondaryAxes(this.encoding);

      if (secondary.length && this.renderModel.validateFormula(secondary[0].formula)[0]) {
        const field = this.renderModel.makeField(secondary[0]);
        if (!Switch.Fields.isNames(field)) {
          this.dimension = this.renderModel.compileField(field);
        }
      }

      if (secondaryDiscrete.length && this.renderModel.validateFormula(secondaryDiscrete[0].formula)[0]) {
        const field = this.renderModel.makeField(secondaryDiscrete[0]);
        this.dimension = this.renderModel.compileField(field);
      }
      this.xFormatType = this.getXFormatType();

      if (
        !EncodingHelpers.validateChartEncodingFields(this.encoding, this.renderModel).length &&
        EncodingHelpers.isChartEncodingComplete(this.encoding, this.chartType)
      ) {
        switch (this.chartType) {
          case 'bar':
            this.configs = merge(this.defaultConfigs, {
              bar: {
                titles: ChartTitle.makeGroupedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'hBar':
            this.configs = merge(this.defaultConfigs, {
              hBar: {
                titles: ChartTitle.makeGroupedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'linePlusBar':
            this.configs = merge(this.defaultConfigs, {
              linePlusBar: {
                titles: ChartTitle.makeComboTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'stackedBar':
            this.configs = merge(this.defaultConfigs, {
              stackedBar: {
                titles: ChartTitle.makeStackedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'hStackedBar':
            this.configs = merge(this.defaultConfigs, {
              hStackedBar: {
                titles: ChartTitle.makeStackedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'stackedBar100':
            this.configs = merge(this.defaultConfigs, {
              stackedBar100: {
                titles: ChartTitle.makeStackedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'hStackedBar100':
            this.configs = merge(this.defaultConfigs, {
              hStackedBar100: {
                titles: ChartTitle.makeStackedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'line':
            this.configs = merge(this.defaultConfigs, {
              line: {
                titles: ChartTitle.makeStackedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'area':
            this.configs = merge(this.defaultConfigs, {
              area: {
                titles: ChartTitle.makeStackedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'totalArea':
            this.configs = merge(this.defaultConfigs, {
              totalArea: {
                titles: ChartTitle.makeStackedTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'pie':
            this.configs = merge(this.defaultConfigs, {
              pie: {
                titles: ChartTitle.makePieTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'donut':
            this.configs = merge(this.defaultConfigs, {
              donut: {
                titles: ChartTitle.makePieTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'histogram':
            this.configs = merge(this.defaultConfigs, {
              histogram: {
                titles: ChartTitle.makeHistogramTitles(this.renderModel, this.encoding),
              },
            });
            break;
          case 'scatter':
            this.configs = merge(this.defaultConfigs, {
              scatter: {
                titles: ChartTitle.makeScatterTitles(this.renderModel, this.encoding),
              },
            });
            break;
        }
      }
    }
    if (changes?.renderModel && changes?.renderModel?.currentValue) {
      this.$scope.$applyAsync(() => {
        if (this.config.titles == null) {
          return;
        }
        this.displayName.options = this.buildDisplayNameOptions();
        this.displayName.selectedField = this.displayName.options[0];
      });
    }
  }

  getXFormatType(): XFormatType {
    if (!this.dimension) {
      return 'NONE';
    }

    if (this.dimension.hasDateFunc) {
      if (this.dimension.hasDateTrunc) {
        return 'DATE';
      } else {
        switch (this.dimension.dateFuncUnit) {
          case DateTime.Unit.Month:
            return 'MONTH';
          case DateTime.Unit.Weekday:
            return 'WEEKDAY';
          case DateTime.Unit.Hour:
            return 'HOUR';
          case DateTime.Unit.Minute:
            return 'MINUTE';
          case DateTime.Unit.Second:
            return 'SECOND';
        }
      }
    } else if (this.dimension.isValid) {
      switch (this.dimension.formulaDataType) {
        case DataType.Name.Decimal:
        case DataType.Name.Float:
        case DataType.Name.Integer:
          return 'NUMBER';
        default:
          return 'NONE';
      }
    }
  }

  save() {
    this.chartService.updateChartFormat(this.mod);
  }

  buildDisplayNameOptions() {
    let options = [];
    if (this.encoding.value.length > 1 && this.renderModel) {
      options = this.encoding.value.map((field) => {
        const modeValueField = this.renderModel.makeField(field);
        return {
          source: field.formula,
          title: modeValueField.name,
          value: this.mod.yAxis?.valueFieldFormatMap?.[field.formula]?.baseFormat?.displayName || modeValueField.name,
        };
      });
    }
    for (const key in this.config.titles) {
      if (this.config.titles[key] !== 'Values' && this.config.titles[key] != null) {
        const newOption = {
          source: key,
          title: this.config.titles[key],
          value: this.resolveOptionValue(key),
        };
        options = [...options, newOption];
      }
    }
    if ((this.chartType === 'pie' || this.chartType === 'donut') && this.encoding.marks.properties.text.values.length) {
      const textField = this.encoding.marks.properties.text.values[0];
      const modeField = this.renderModel.makeField(textField);
      const newOption = {
        source: textField.formula,
        title: modeField.name,
        value: this.mod.yAxis?.valueFieldFormatMap?.[textField.formula]?.baseFormat?.displayName || modeField.name,
      };
      options = [...options, newOption];
    }
    return options;
  }

  resolveOptionValue(configTitlesKey: string) {
    const defaultTitle = this.config.titles[configTitlesKey];
    if (this.encoding.value.length === 1 && configTitlesKey === 'yAxis') {
      return (
        this.mod.yAxis?.valueFieldFormatMap?.[this.encoding.value[0].formula]?.baseFormat.displayName || defaultTitle
      );
    }
    return (
      this.mod[configTitlesKey]?.baseFormat?.displayName ||
      this.mod[configTitlesKey]?.title ||
      this.config.titles[configTitlesKey]
    );
  }

  udpateDisplayName() {
    const { source, value } = this.displayName.selectedField;
    const displayName: ChartTypes.BaseFieldFormatSubset = {
      [ChartTypes.FieldFormatTypes.Base]: {
        displayName: value as string,
      },
    };

    const valueField: EncField | undefined = this.encoding.value.length === 1 ? this.encoding.value[0] : undefined;

    switch (source) {
      case 'yAxis':
        this.mod.yAxis = {
          ...this.mod.yAxis,
          valueFieldFormatMap: {
            [valueField.formula]: {
              ...displayName,
            },
          },
        };
        break;
      case 'xAxis':
      case 'legend':
      case 'y2Axis':
        this.mod[source] = {
          ...this.mod[source],
          ...displayName,
        };
        break;
      default:
        if (!this.mod.yAxis.valueFieldFormatMap) {
          this.mod.yAxis = {
            ...this.mod.yAxis,
            valueFieldFormatMap: {
              [source]: {
                ...displayName,
              },
            },
          };
          break;
        }
        this.mod.yAxis.valueFieldFormatMap[source] = {
          ...displayName,
        };
    }
    this.save();
  }

  toggleNullToZero() {
    this.mod.nullToZero = !this.mod.nullToZero;
    this.save();
  }

  toggleDrilldown() {
    this.mod.drilldownEnabled = !this.mod.drilldownEnabled;
    this.save();
  }

  toggleUseMuzeRenderer() {
    const title = this.mod.useMuzeRenderer ? 'Enable JavaScript customization?' : 'Disable JavaScript customization?';
    const warningMsg = this.mod.useMuzeRenderer
      ? 'Charts with JavaScript customization will not receive future enhancements.<br>Do you want to continue?'
      : 'Disabling JavaScript customization for existing charts will make any added customizations unavailable.' +
        '<br><br>' +
        'Additionally, Reports that use JavaScript customization may generate errors as a result.';
    const confirmText = this.mod.useMuzeRenderer
      ? 'Enable JavaScript customization'
      : 'Disable JavaScript customization';

    this.confirmDialogService.open({
      dialogTitle: title,
      dialogText: warningMsg,
      confirmText: confirmText,
      confirmAction: this.continueToggleUseMuzeRenderer.bind(this),
      confirmButtonColor: 'alert',
    });
  }

  continueToggleUseMuzeRenderer() {
    this.mod.useMuzeRenderer = !this.mod.useMuzeRenderer;
    this.save();

    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.Sidebar,
      action: this.mod.useMuzeRenderer ? 'Disabled JS Customization' : 'Enabled JS Customization',
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
      chartType: this.chartType,
    });
  }

  validateYAxisMod(yAxisKey: string, save = true) {
    if (this.mod) {
      this.yAxisHasError[yAxisKey] = !validateYAxis(this.mod[yAxisKey]);
      if (save) {
        this.save();
      }
    }
  }

  protected colorSchemeCssClass() {
    return FlatTableColorScheme.getCssClass(this.mod.flatTable.general.colorScheme);
  }
}
export const ChartFormatComponent: angular.IComponentOptions = {
  bindings: {
    model: '<',
    viewName: '<',
    renderModel: '<',
    encoding: '<',
    format: '<',
    readOnly: '<',
    chartType: '<',
    vizToken: '<',
    chartService: '<',
    owner: '<',
  },
  controller: ChartFormatController,
  controllerAs: 'ctrl',

  template: `
  <div ng-if="ctrl.useMuzeRenderer">
    <h3 class="setting-title">Display Label</h3>
    <div class="form-field">
      <label class="axis-label">Select a field to format</label>
      <select class="form-control form-control-sm form-control-block" ng-model="ctrl.displayName.selectedField" ng-change="ctrl.updateSelectedField()" ng-options="item as item.title for item in ctrl.displayName.options" ng-disabled="!ctrl.displayName.selectedField">
      </select>
      <div class="display-label-input-container">
        <label class="axis-label">Display Label</label>
        <capra-tooltip
          [tooltip-content]="ctrl.DisplayLabelTooltipContent"
          [tooltip-interactive]="false"
          [tooltip-delay]="[750, 0]"
          tooltip-placement="bottom"
        >
          <capra-icon
            icon="icon-info-outline"
            class="info-icon"
          ></capra-icon>
        </capra-tooltip>
      </div>
      <input type="text"
        class="form-control form-control-sm axis-title-input"
        placeholder="{{ ctrl.displayName.selectedField.value || ctrl.displayName.selectedField.title }}"
        ng-model-options="{ updateOn: 'blur' }"
        ng-model="ctrl.displayName.selectedField.value"
        ng-change="ctrl.udpateDisplayName()"
        mode-enter />
    </div>
  </div>
    <div ng-if="ctrl.config.planeType === 'cartesian'" class="cartesian" ng-class="{ horizontal: ctrl.isHorizontal }">
      <div class="secondary-axis">
        <h3 class="setting-title">{{ ctrl.isHorizontal ? 'Y' : 'X' }}-Axis</h3>
        <div class="form-field">
          <label class="axis-label">Title</label>
          <input type="text"
                 class="form-control form-control-sm axis-title-input"
                 placeholder="{{ ctrl.mod.xAxis.baseFormat.displayName  || ctrl.config.titles.xAxis }}"
                 ng-model-options="{ updateOn: 'blur' }"
                 ng-model="ctrl.mod.xAxis.title"
                 ng-change="ctrl.save()"
                 mode-enter />
        </div>

        <div class="form-field check-field">
          <span class="checkbox-icon">
            <input type="checkbox" id="xshowtitle" value="xshowtitle" ng-model="ctrl.mod.xAxis.titleEnabled" ng-change="ctrl.save()" />
            <label class="inline-icon icon-editor-checkbox-light-unchecked" for="xshowtitle">
            Show Title
            </label>
          </span>
        </div>

        <div class="form-field check-field" ng-if="ctrl.config.showAxisLabels">
          <span class="checkbox-icon">
            <input type="checkbox" id="showAxisLabels" value="showAxisLabels" ng-model="ctrl.mod.xAxis.showAxisLabels" ng-change="ctrl.save()" />
            <label class="inline-icon icon-editor-checkbox-light-unchecked" for="showAxisLabels">
            Show axis labels
            </label>
          </span>
        </div>
        <div class="form-field" ng-if="ctrl.config.showAxisLabels">
          <label class="axis-label">Axis labels</label>
          <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.xAxis.axisLabels" ng-change="ctrl.save()" ng-options="item.value as item.name for item in ::ctrl.axisLabels">
            <option value="">Auto</option>
          </select>
        </div>

        <div class="form-field check-field" ng-if="ctrl.config.hideableValues && ctrl.encoding.y.length <= 1">
          <span class="checkbox-icon">
            <input type="checkbox" id="xshowval" value="angle" ng-model="ctrl.mod.showValues" ng-change="ctrl.save()" />
            <label class="inline-icon icon-editor-checkbox-light-unchecked" for="xshowval">
            Show Bar Values
            </label>
          </span>
        </div>

        <div class="form-field">
          <label class="axis-label">Rotate Label</label>
          <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.xAxis.rotate" ng-change="ctrl.save()" ng-options="item.value as item.name for item in ::ctrl.rotations">
          </select>
        </div>

        <div class="form-field" ng-show="ctrl.xFormatType === 'NUMBER'" ng-if="ctrl.config.chooseXRange">
          <label class="axis-label">Range</label>
          <div class="range-input-container">
            <input class="form-control form-control-sm range-input"
                   placeholder="min"
                   type="number"
                   ng-model="ctrl.mod.xAxis.numberRange.min"
                   ng-model-options="{ updateOn: 'blur' }"
                   ng-change="ctrl.save()"
                   mode-enter />
            <span class="range-input-separator">&#8722;</span>
            <input class="form-control form-control-sm range-input pull-right"
                   placeholder="max"
                   type="number"
                   ng-model="ctrl.mod.xAxis.numberRange.max"
                   ng-model-options="{ updateOn: 'blur' }"
                   ng-change="ctrl.save();"
                   mode-enter />
          </div>
        </div>

        <div class="form-field" ng-if="ctrl.xFormatType === 'NUMBER'">
          <div class="fields-container">
            <div class="half-width">
              <label class="axis-label">Format</label>
              <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.xAxis.numberFormat.format" ng-change="ctrl.save()" ng-options="item.value as item.name for item in ::ctrl.formats">
                <option value="">Auto</option>
              </select>
            </div>
            <div class="half-width">
              <label class="axis-label">Precision</label>
              <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.xAxis.numberFormat.precision" ng-change="ctrl.save()" ng-options="item.value as item.name for item in ::ctrl.precisions">
                <option value="">Auto</option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-field" ng-show="ctrl.xFormatType === 'DATE'">
          <label class="axis-label">Format</label>
          <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.xAxis.dateFormat" ng-change="ctrl.save()" ng-options="item.value as item.name for item in ::ctrl.dateFormats">
          </select>
        </div>
      </div>
      <div class="primary-axis">
        <h3 class="setting-title">{{ ctrl.isHorizontal ? 'X' : 'Y' }}-Axis</h3>

        <div class="form-field">
          <label class="axis-label">Title</label>
          <input type="text"
                 class="form-control form-control-sm axis-title-input"
                 placeholder="{{ ctrl.encoding.value.length === 1 ? ctrl.mod.yAxis.valueFieldFormatMap[ctrl.encoding.value[0].formula].baseFormat.displayName || ctrl.config.titles.yAxis : ctrl.mod.yAxis.baseFormat.displayName || ctrl.config.titles.yAxis }}"
                 ng-model-options="{ updateOn: 'blur' }"
                 ng-model="ctrl.mod.yAxis.title"
                 ng-change="ctrl.save()"
                 mode-enter />
        </div>

        <div class="form-field check-field">
          <span class="checkbox-icon">
            <input type="checkbox" id="yshowtitle" value="yshowtitle" ng-model="ctrl.mod.yAxis.titleEnabled" ng-change="ctrl.save()" />
            <label class="inline-icon icon-editor-checkbox-light-unchecked" for="yshowtitle">
            Show Title
            </label>
          </span>
        </div>

        <div class="form-field" ng-class="{clearfix: !ctrl.config.yNumberRangeMin}" ng-if="ctrl.config.yNumberRange">
          <label class="axis-label">Range</label>
          <div class="range-input-container">
            {{ !ctrl.config.yNumberRangeMin ? 0 : '' }}
            <input ng-if="ctrl.config.yNumberRangeMin"
                   class="form-control form-control-sm range-input"
                   ng-class="{ 'ng-invalid': ctrl.yAxisHasError[ctrl.Y1_AXIS], 'ng-untouched': !ctrl.yAxisHasError[ctrl.Y1_AXIS] }"
                   placeholder="min"
                   type="number"
                   ng-model="ctrl.mod.yAxis.numberRange.min"
                   ng-model-options="{ updateOn: 'blur' }"
                   ng-change="ctrl.validateYAxisMod(ctrl.Y1_AXIS);"
                   mode-enter />
            <span class="range-input-separator">&#8722;</span>
            <input class="form-control form-control-sm pull-right"
                   ng-class="{'range-input': ctrl.config.yNumberRangeMin, 'range-input-lg': !ctrl.config.yNumberRangeMin}"
                   placeholder="max" type="number"
                   ng-model="ctrl.mod.yAxis.numberRange.max"
                   ng-model-options="{ updateOn: 'blur' }"
                   ng-change="ctrl.save();"
                   mode-enter />
          </div>
          <div class="invalid-error" ng-if="ctrl.yAxisHasError[ctrl.Y1_AXIS]">Must be greater than 0</div>
        </div>

        <div class="form-field" ng-if="ctrl.config.yNumberFormat">
          <div class="fields-container">
            <div class="half-width">
              <label class="axis-label">Format</label>
              <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.yAxis.numberFormat.format" ng-options="item.value as item.name for item in ::ctrl.formats" ng-change="ctrl.save()">
                <option value="">Auto</option>
              </select>
            </div>
            <div class="half-width">
              <label class="axis-label">Precision</label>
              <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.yAxis.numberFormat.precision" ng-options="item.value as item.name for item in ::ctrl.precisions" ng-change="ctrl.save()">
                <option value="">Auto</option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-field" ng-if="ctrl.config.showScaleType">
          <label class="axis-label">Scale Type</label>
          <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.yAxis.scaleType" ng-change="ctrl.validateYAxisMod('yAxis')" ng-options="item.value as item.name for item in ::ctrl.scaleTypes"></select>
        </div>

        <div ng-if="ctrl.config.y2Axis">
          <h3 class="setting-title">Y2-Axis</h3>

          <div class="form-field">
            <label class="axis-label">Title</label>
            <input type="text"
                   class="form-control form-control-sm axis-title-input"
                   placeholder="{{ ctrl.mod.y2Axis.baseFormat.displayName || ctrl.config.titles.y2Axis  }}"
                   ng-model-options="{ updateOn: 'blur' }"
                   ng-model="ctrl.mod.y2Axis.title"
                   ng-change="ctrl.save()"
                   mode-enter />
          </div>

          <div class="form-field check-field">
            <span class="checkbox-icon">
              <input type="checkbox" id="y2showtitle" value="y2showtitle" ng-model="ctrl.mod.y2Axis.titleEnabled" ng-change="ctrl.save()" />
              <label class="inline-icon icon-editor-checkbox-light-unchecked" for="y2showtitle">
              Show Title
              </label>
            </span>
          </div>

          <div class="form-field" ng-class="{clearfix: !ctrl.config.yNumberRangeMin}" ng-if="ctrl.config.yNumberRange">
            <label class="axis-label">Range</label>
            <div class="range-input-container">
              {{ !ctrl.config.yNumberRangeMin ? 0 : '' }}

              <input ng-if="ctrl.config.yNumberRangeMin"
                     class="form-control form-control-sm range-input"
                     ng-class="{ 'ng-invalid': ctrl.yAxisHasError[ctrl.Y2_AXIS], 'ng-untouched': !ctrl.yAxisHasError[ctrl.Y2_AXIS] }"
                     placeholder="min"
                     type="number"
                     ng-model="ctrl.mod.y2Axis.numberRange.min"
                     ng-model-options="{ updateOn: 'blur' }"
                     ng-change="ctrl.validateYAxisMod(ctrl.Y2_AXIS)"
                     mode-enter />
              <span class="range-input-separator">&#8722;</span>
              <input class="form-control form-control-sm pull-right"
                     ng-class="{'range-input': ctrl.config.yNumberRangeMin, 'range-input-lg': !ctrl.config.yNumberRangeMin}"
                     placeholder="max"
                     type="number"
                     ng-model="ctrl.mod.y2Axis.numberRange.max"
                     ng-model-options="{ updateOn: 'blur' }"
                     ng-change="ctrl.save();"
                     mode-enter />
            </div>
            <div class="invalid-error" ng-if="ctrl.yAxisHasError[ctrl.Y2_AXIS]">Must be greater than 0</div>
          </div>

          <div class="form-field" ng-if="ctrl.config.yNumberFormat">
            <div class="fields-container">
              <div class="half-width">
                <label class="axis-label">Format</label>
                <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.y2Axis.numberFormat.format" ng-options="item.value as item.name for item in ::ctrl.formats" ng-change="ctrl.save()">
                  <option value="">Auto</option>
                </select>
              </div>
              <div class="half-width">
                <label class="axis-label">Precision</label>
                <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.y2Axis.numberFormat.precision" ng-options="item.value as item.name for item in ::ctrl.precisions" ng-change="ctrl.save()">
                  <option value="">Auto</option>
                </select>
              </div>
            </div>
          </div>

          <div class="form-field">
            <label class="axis-label">Scale Type</label>
            <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.y2Axis.scaleType" ng-change="ctrl.validateYAxisMod('y2Axis')" ng-options="item.value as item.name for item in ::ctrl.scaleTypes"></select>
          </div>
        </div>
      </div>
    </div>

    <!-- Polar Planes -->

    <div ng-if="ctrl.config.planeType === 'polar'">
      <h3 class="setting-title no-border-top">Chart Options</h3>
      <div class="form-field check-field">
        <span class="checkbox-icon">
          <input type="checkbox" id="pieshowval" value="angle" ng-model="ctrl.mod.showValues" ng-change="ctrl.save()" />
          <label class="inline-icon icon-editor-checkbox-light-unchecked" for="pieshowval">
          Show Chart Values
          </label>
        </span>
      </div>
      <div class="form-field check-field">
        <span class="checkbox-icon">
          <input type="checkbox" id="pieformat" value="angle" ng-model="ctrl.mod.pieLabelsNotPercentages" ng-change="ctrl.save()" />
          <label class="inline-icon icon-editor-checkbox-light-unchecked" for="pieformat">
          Use Color Values For Labels
          </label>
        </span>
      </div>
    </div>

    <div class="settings-group" ng-if="!ctrl.isTableType && ctrl.config.showLegendFormatOptions">
      <h3 class="setting-title">Legend</h3>

      <div class="form-field check-field">
        <span class="checkbox-icon">
          <input type="checkbox" id="showlegend" value="showLegend" ng-model="ctrl.mod.legend.enabled" ng-change="ctrl.save()" />
          <label class="inline-icon icon-editor-checkbox-light-unchecked" for="showlegend">
          Show Legend
          </label>
        </span>
      </div>

      <div class="form-field">
        <label class="axis-label">Position</label>
        <select class="form-control form-control-sm form-control-block" ng-model="ctrl.mod.legend.position" ng-change="ctrl.save()" ng-options="item.value as item.name for item in ::ctrl.legendPositions">
        </select>
      </div>

      <div class="form-field">
        <label class="axis-label">Title</label>
        <input type="text"
              class="form-control form-control-sm axis-title-input"
              placeholder="{{ ctrl.mod.legend.baseFormat.displayName || ctrl.config.titles.legend }}"
              ng-model-options="{ updateOn: 'blur' }"
              ng-model="ctrl.mod.legend.title"
              ng-change="ctrl.save()"
              mode-enter />
      </div>

      <div class="form-field check-field">
        <span class="checkbox-icon">
          <input type="checkbox" id="legendshowtitle" value="legendshowtitle" ng-model="ctrl.mod.legend.titleEnabled" ng-change="ctrl.save()" />
          <label class="inline-icon icon-editor-checkbox-light-unchecked" for="legendshowtitle">
          Show Title
          </label>
        </span>
      </div>
    </div>

    <div class="settings-group" ng-if="ctrl.showDetailsGroup">
      <h3 class="setting-title">Details</h3>
      <div class="toggle-field" ng-if="ctrl.config.nullToZeroCheckbox">
        <mode-toggle-switch
          class="toggle"
          size="small"
          custom-id="toggle-line-null-to-zero"
          [state]="ctrl.mod.nullToZero"
          (state-change)="ctrl.toggleNullToZero()"
          data-test="toggle-line-null-to-zero"
        ></mode-toggle-switch>
        <label class="toggle-label">Show Null Values as Zero</label>
      </div>
      <div class="toggle-field" ng-if="ctrl.owner.plan_code === 'plus'">
        <mode-toggle-switch
          class="toggle"
          size="small"
          custom-id="toggle-drilldown"
          [state]="ctrl.mod.drilldownEnabled"
          (state-change)="ctrl.toggleDrilldown()"
          data-test="toggle-drill-down"
        ></mode-toggle-switch>
        <label class="toggle-label">Drilldowns</label>
      </div>
        <div class="field-with-description" ng-if="ctrl.showUseMuzeRenderer">
          <capra-tooltip
            class="center"
            [tooltip-content]="ctrl.JS_CustomizationDisabledTip"
            [tooltip-delay]="[500, 0]"
            [tooltip-is-disabled]="ctrl.allowHighCharts"
            tooltip-placement="bottom"
            >
            <mode-toggle-switch
              class="toggle center"
              size="small"
              custom-id="toggle-use-muze-renderer"
              [disabled]="!ctrl.allowHighCharts"
              [state]="!ctrl.mod.useMuzeRenderer"
              (state-change)="ctrl.toggleUseMuzeRenderer()"
              data-test="toggle-use-muze-renderer"
            >
            </mode-toggle-switch>
          </capra-tooltip>
          <label class="toggle-label">JavaScript customization</label>
          <div></div>
          <div class="field-description">{{ ctrl.JS_CustomizationText }}</div>
        </div>
    </div>
    <div class="settings group" ng-if="ctrl.isTableType">
      <h3 class="setting-title">Style</h3>

      <div class="form-field form-field-color-scheme">
        <div class="form-item-content">
          <table class="table-scheme-thumb" ng-class="[ctrl.colorSchemeCssClass()]">
            <tr><td></td><td></td></tr>
            <tr><td></td><td></td></tr>
            <tr><td></td><td></td></tr>
            <tr><td></td><td></td></tr>
          </table>
        </div>
        <div class="form-item">
          <label class="axis-label">Alternating Colors</label>
          <select class="form-control form-control-sm form-control-block"
            ng-model="ctrl.mod.flatTable.general.colorScheme"
            ng-change="ctrl.save()"
            ng-options="item.value as item.name for item in ctrl.colorSchemes"
          ></select>
        </div>
      </div>

      <div class="form-field">
        <label class="axis-label">Row Height</label>
        <select class="form-control form-control-sm form-control-block"
          ng-model="ctrl.mod.flatTable.general.density"
          ng-change="ctrl.save()"
          ng-options="item.value as item.name for item in ctrl.densities"
        ></select>
      </div>

      <h3 class="setting-title">Details</h3>

      <div class="form-field check-field">
        <span class="checkbox-icon">
          <input type="checkbox" id="columnGridlines" value="columnGridlines" ng-model="ctrl.mod.flatTable.general.colGridlines" ng-change="ctrl.save()" />
          <label class="inline-icon icon-editor-checkbox-light-unchecked" for="columnGridlines">
            Show Column Gridlines
          </label>
        </span>
      </div>

      <div class="form-field check-field">
        <span class="checkbox-icon">
          <input type="checkbox" id="rowGridlines" value="rowGridlines" ng-model="ctrl.mod.flatTable.general.rowGridlines" ng-change="ctrl.save()" />
          <label class="inline-icon icon-editor-checkbox-light-unchecked" for="rowGridlines">
            Show Row Gridlines
          </label>
        </span>
      </div>

      <div class="form-field check-field">
        <span class="checkbox-icon">
          <input type="checkbox" id="rowNumbers" value="rowNumbers" ng-model="ctrl.mod.flatTable.general.rowNumbers" ng-change="ctrl.save()" />
          <label class="inline-icon icon-editor-checkbox-light-unchecked" for="rowNumbers">
            Show Row Numbers
          </label>
        </span>
      </div>

      <div class="form-field check-field">
        <span class="checkbox-icon">
          <input type="checkbox" id="dataProfiling" value="dataProfilingEnabled" ng-model="ctrl.mod.flatTable.general.dataProfilingEnabled" ng-change="ctrl.save()" />
          <label class="inline-icon icon-editor-checkbox-light-unchecked" for="dataProfiling">
            Show Data Profile
          </label>
        </span>
      </div>
      <div class="settings-group" ng-if="ctrl.isTableType && ctrl.config.showLegendFormatOptions">
        <h3 class="setting-title">Legend</h3>

        <div class="form-field check-field">
          <span class="checkbox-icon">
            <input type="checkbox" id="showlegend" value="showLegend" ng-model="ctrl.mod.legend.enabled" ng-change="ctrl.save()" />
            <label class="inline-icon icon-editor-checkbox-light-unchecked" for="showlegend">
            Show Legend
            </label>
          </span>
        </div>
      </div>
    </div>
`,
};
